import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ProjectLogo } from "./Logo";
import ThemeToggle from "./ThemeToggle";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLang } from "../i18n";

const anchors = [
  { label: "Thesis", hash: "thesis", k: "nav.thesis" },
  { label: "Disciplines", hash: "disciplines", k: "nav.disciplines" },
  { label: "Horizon", hash: "horizon", k: "nav.horizon" },
];
const pages = [{ label: "Apply", to: "/apply", k: "nav.join" }];

const Header = ({ theme = "dark", onToggleTheme }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [assessOpen, setAssessOpen] = useState(false);
  const [mobileAssessOpen, setMobileAssessOpen] = useState(false);
  const assessRef = useRef(null);
  const location = useLocation();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setAssessOpen(false);
    setMobileAssessOpen(false);
  }, [location]);

  useEffect(() => {
    if (!assessOpen) return undefined;
    const onDoc = (e) => {
      if (assessRef.current && !assessRef.current.contains(e.target)) setAssessOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [assessOpen]);

  const navCls = (active) =>
    `link-underline whitespace-nowrap text-base font-medium transition-colors duration-300 ${
      active ? "text-white" : "text-fog hover:text-white"
    }`;

  const menuItem = (i) => ({
    initial: { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.45, delay: 0.08 + i * 0.06, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-xl transition-colors duration-500 ${
        scrolled || menuOpen ? "bg-void/95 border-white/10" : "bg-void/40 border-white/5"
      }`}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 py-3 md:py-4 sm:px-6">
        <Link
          to="/"
          data-testid="brand-logo-link"
          onClick={(e) => {
            if (location.pathname === "/") {
              e.preventDefault();
              if (window.__lenis) window.__lenis.scrollTo(0);
              else window.scrollTo(0, 0);
            }
          }}
          className="group flex min-w-0 flex-1 items-center gap-2 min-[400px]:gap-3 lg:flex-none"
        >
          <ProjectLogo className="h-9 w-9 shrink-0" />
          <span className="block whitespace-nowrap font-display font-bold tracking-tight text-white text-[clamp(13px,4.2vw,17px)] lg:hidden xl:block xl:text-lg">
            The Lifespan Project
          </span>
        </Link>
        <nav data-testid="main-nav" className="hidden items-center gap-5 xl:gap-8 lg:flex">
          {anchors.map((l) => (
            <Link
              key={l.label}
              to={`/#${l.hash}`}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={navCls(false)}
            >
              {t(l.k, l.label)}
            </Link>
          ))}
          {pages.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className={navCls(location.pathname === l.to)}
            >
              {t(l.k, l.label)}
            </Link>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <div className="relative" ref={assessRef}>
            <button
              type="button"
              data-testid="nav-assessment-pill"
              aria-expanded={assessOpen}
              onClick={() => setAssessOpen((o) => !o)}
              className="badge-animated flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-semibold text-white transition-opacity duration-300 hover:opacity-90"
            >
              Free Lifespan Markers
              <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${assessOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {assessOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="nav-assessment-menu"
                  className="absolute right-0 top-full z-50 mt-3 w-[24rem] overflow-hidden rounded-2xl border border-white/10 bg-void/95 p-2 shadow-2xl backdrop-blur-xl"
                >
                  <Link
                    to="/assessment?src=nav"
                    data-testid="nav-assessment-l0"
                    onClick={() => setAssessOpen(false)}
                    className="block rounded-xl px-4 py-3 transition-colors duration-200 hover:bg-white/5"
                  >
                    <p className="text-sm font-semibold text-white">
                      <span className="mr-1.5 rounded-full bg-amber-400/15 px-1.5 py-0.5 align-middle text-[9px] font-bold uppercase tracking-wider text-amber-300">Beta</span>
                      General Lifespan Assessment: Level 0
                    </p>
                    <p className="mt-0.5 text-xs font-light text-fog">Body, mind and lifespan markers in 30 taps.</p>
                  </Link>
                  <Link
                    to="/assessment/level-1?src=nav"
                    data-testid="nav-assessment-l1"
                    onClick={() => setAssessOpen(false)}
                    className="block rounded-xl px-4 py-3 transition-colors duration-200 hover:bg-white/5"
                  >
                    <p className="text-sm font-semibold text-white">
                      <span className="mr-1.5 rounded-full bg-amber-400/15 px-1.5 py-0.5 align-middle text-[9px] font-bold uppercase tracking-wider text-amber-300">Beta</span>
                      Current Mindset Marker Assessment: Level 1
                    </p>
                    <p className="mt-0.5 text-xs font-light text-fog">Mental Marker Scan with camera and voice.</p>
                  </Link>
                  <Link
                    to="/assessment/level-2?src=nav"
                    data-testid="nav-assessment-l2"
                    onClick={() => setAssessOpen(false)}
                    className="block rounded-xl px-4 py-3 transition-colors duration-200 hover:bg-white/5"
                  >
                    <p className="text-sm font-semibold text-white">
                      <span className="mr-1.5 rounded-full bg-amber-400/15 px-1.5 py-0.5 align-middle text-[9px] font-bold uppercase tracking-wider text-amber-300">Beta</span>
                      Geographical RiskRadar Marker: Level 2
                    </p>
                    <p className="mt-0.5 text-xs font-light text-fog">Your city's external risk zones on a live map.</p>
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <LanguageSwitcher variant="desktop" />
          <ThemeToggle theme={theme} onToggle={onToggleTheme} />
          <Link
            to="/subscribe"
            data-testid="nav-link-back-us"
            className="bg-gradient-green group flex whitespace-nowrap items-center gap-2 rounded-full px-5 py-2.5 text-base font-semibold text-black transition-opacity duration-300 hover:opacity-90"
          >
            {t("nav.backus", "Subscribe")}
          </Link>
          <Link
            to="/back-us"
            data-testid="header-invest-button"
            className="bg-gradient-accent group flex whitespace-nowrap items-center gap-2 rounded-full px-5 py-2.5 text-base font-semibold text-white transition-opacity duration-300 hover:opacity-90"
          >
            {t("nav.invest", "Back Us")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
        <div className="flex items-center gap-2.5 lg:hidden">
          <ThemeToggle theme={theme} onToggle={onToggleTheme} compact />
          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 text-white"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="max-h-[calc(100dvh-60px)] overflow-y-auto overflow-x-hidden border-t border-white/10 bg-void/95 px-4 py-6 backdrop-blur-xl lg:hidden"
        >
          <nav className="flex flex-col gap-5">
            <motion.div {...menuItem(0)} className="flex flex-col gap-2">
              <button
                type="button"
                data-testid="mobile-assessment-dropdown"
                aria-expanded={mobileAssessOpen}
                onClick={() => setMobileAssessOpen((o) => !o)}
                className="badge-animated flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-[13px] font-semibold text-white"
              >
                Free Lifespan Markers
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${mobileAssessOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {mobileAssessOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                    data-testid="mobile-assessment-menu"
                  >
                    <div className="flex flex-col gap-2 pt-1">
                      <Link
                        to="/assessment?src=nav"
                        data-testid="mobile-nav-assessment-l0"
                        onClick={() => setMenuOpen(false)}
                        className="flex flex-col items-start rounded-2xl border border-cyan-300/25 bg-cyan-400/5 px-4 py-2.5 text-left"
                      >
                        <p className="text-[13px] font-semibold leading-snug text-white">
                          <span className="mr-1.5 rounded-full bg-amber-400/15 px-1.5 py-0.5 align-middle text-[8px] font-bold uppercase tracking-wider text-amber-300">Beta</span>
                          General Lifespan Assessment: Level 0
                        </p>
                        <p className="mt-0.5 text-[11px] font-light text-fog">Body, mind and lifespan markers in 30 taps.</p>
                      </Link>
                      <Link
                        to="/assessment/level-1?src=nav"
                        data-testid="mobile-nav-assessment-l1"
                        onClick={() => setMenuOpen(false)}
                        className="flex flex-col items-start rounded-2xl border border-violet-300/30 bg-violet-400/10 px-4 py-2.5 text-left"
                      >
                        <p className="text-[13px] font-semibold leading-snug text-white">
                          <span className="mr-1.5 rounded-full bg-amber-400/15 px-1.5 py-0.5 align-middle text-[8px] font-bold uppercase tracking-wider text-amber-300">Beta</span>
                          Current Mindset Marker Assessment: Level 1
                        </p>
                        <p className="mt-0.5 text-[11px] font-light text-fog">Mental Marker Scan with camera and voice.</p>
                      </Link>
                      <Link
                        to="/assessment/level-2?src=nav"
                        data-testid="mobile-nav-assessment-l2"
                        onClick={() => setMenuOpen(false)}
                        className="flex flex-col items-start rounded-2xl border border-green-300/30 bg-green-400/10 px-4 py-2.5 text-left"
                      >
                        <p className="text-[13px] font-semibold leading-snug text-white">
                          <span className="mr-1.5 rounded-full bg-amber-400/15 px-1.5 py-0.5 align-middle text-[8px] font-bold uppercase tracking-wider text-amber-300">Beta</span>
                          Geographical RiskRadar Marker: Level 2
                        </p>
                        <p className="mt-0.5 text-[11px] font-light text-fog">Your city's external risk zones on a live map.</p>
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
            {anchors.map((l, i) => (
              <motion.div key={l.label} {...menuItem(i + 1)}>
                <Link
                  to={`/#${l.hash}`}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  onClick={() => setMenuOpen(false)}
                  className="text-sm font-medium text-fog transition-colors duration-300 hover:text-white"
                >
                  {t(l.k, l.label)}
                </Link>
              </motion.div>
            ))}
            {pages.map((l, i) => (
              <motion.div key={l.label} {...menuItem(anchors.length + 1 + i)}>
                <Link
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
                  onClick={() => setMenuOpen(false)}
                  className="text-sm font-medium text-fog transition-colors duration-300 hover:text-white"
                >
                  {t(l.k, l.label)}
                </Link>
              </motion.div>
            ))}
          </nav>
          <motion.div {...menuItem(anchors.length + pages.length + 1)} className="mt-5 grid grid-cols-2 gap-3">
            <Link
              to="/subscribe"
              data-testid="mobile-back-us-button"
              onClick={() => setMenuOpen(false)}
              className="bg-gradient-green flex items-center justify-center gap-1.5 rounded-full px-3 py-2.5 text-[13px] font-bold text-black"
            >
              {t("nav.backus", "Subscribe")}
            </Link>
            <Link
              to="/back-us"
              data-testid="mobile-invest-button"
              onClick={() => setMenuOpen(false)}
              className="bg-gradient-accent flex items-center justify-center gap-1.5 rounded-full px-3 py-2.5 text-[13px] font-semibold text-white"
            >
              {t("nav.invest", "Back Us")}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </motion.div>
          <motion.div {...menuItem(anchors.length + pages.length + 2)} className="mt-5 border-t border-white/10 pt-4">
            <LanguageSwitcher variant="menu" onSelect={() => setMenuOpen(false)} />
          </motion.div>
        </div>
      )}
    </header>
  );
};

export default Header;
